import { File, FileImage, FileText } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import blobService from "../../../../services/blobService";
import { BucketContext } from "../../../../types/blob";

interface FileThumbnailProps {
  fileName: string;
  contentType: string;
  context: BucketContext;
  fileSubfolder?: string;
  size?: "sm" | "md";
}

const FileThumbnail: React.FC<FileThumbnailProps> = ({
  fileName,
  contentType,
  context,
  fileSubfolder,
  size = "sm",
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const isImage = blobService.isImage(contentType);
  const isPdf = blobService.isPdf(contentType);

  const sizeClasses = size === "sm" ? "w-10 h-10" : "w-16 h-16";
  const iconSize = size === "sm" ? "w-5 h-5" : "w-8 h-8";

  useEffect(() => {
    if (!isImage) return;

    let cancelled = false;
    const loadThumbnail = async () => {
      setLoading(true);
      setError(false);

      try {
        let blob: Blob;
        if (context.bucketType === "provider") {
          blob = await blobService.downloadProviderFile(
            context.providerId,
            fileName,
            fileSubfolder || undefined
          );
        } else {
          // For RunPlutus, use fileSubfolder if provided, otherwise context.folder
          const folder = fileSubfolder || context.folder;
          blob = await blobService.downloadRunPlutusFile(folder, fileName);
        }

        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          setThumbnailUrl(url);
        }
      } catch (err) {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadThumbnail();

    return () => {
      cancelled = true;
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [fileName, context, isImage, fileSubfolder]);

  // Cleanup URL on unmount
  useEffect(() => {
    return () => {
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [thumbnailUrl]);

  const baseClasses = `${sizeClasses} rounded flex items-center justify-center flex-shrink-0 overflow-hidden`;

  // Image thumbnail
  if (isImage && thumbnailUrl && !error) {
    return (
      <div className={baseClasses}>
        <img
          src={thumbnailUrl}
          alt={fileName}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  // Loading state for images
  if (isImage && loading) {
    return (
      <div
        className={`${baseClasses} ${isDarkMode ? "bg-rp-surface-raised" : "bg-gray-100"}`}
      >
        <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Icon based on file type
  const getIcon = () => {
    if (isImage || error) {
      return (
        <FileImage
          className={`${iconSize} ${isDarkMode ? "text-blue-400" : "text-blue-500"}`}
        />
      );
    }
    if (isPdf) {
      return (
        <FileText
          className={`${iconSize} ${isDarkMode ? "text-red-400" : "text-red-500"}`}
        />
      );
    }
    return (
      <File
        className={`${iconSize} ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
      />
    );
  };

  return (
    <div
      className={`${baseClasses} ${isDarkMode ? "bg-rp-surface-raised" : "bg-gray-100"}`}
    >
      {getIcon()}
    </div>
  );
};

export default FileThumbnail;

